import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Lightbulb, RotateCcw } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { CarouselArrow, CarouselDots, CarouselHint, CARD_SHADOW, CARD_SHADOW_HOVER } from "./carousel-ui";

type Niche = {
  id: string;
  image: string;
  imageAlt: { pt: string; en: string };
  hook: { pt: string; en: string };
  title: { pt: string; en: string };
  description: { pt: string; en: string };
  steps: { pt: string[]; en: string[] };
  accent: string;
};

const niches: Niche[] = [
  {
    id: "creators",
    image: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Creator digital gravando conteúdo", en: "Digital creator recording content" },
    hook: { pt: "Motor de crescimento", en: "Audience growth engine" },
    title: { pt: "Creators Digitais", en: "Digital Creators" },
    description: {
      pt: "Converta tráfego de perfil em inscritos, leads e clientes com links inteligentes e analytics embutido.",
      en: "Turn profile traffic into subscribers, leads and clients with smart links and built-in analytics.",
    },
    steps: {
      pt: ["Organize links por tema ou plataforma", "Adicione captura de e-mail direto na bio", "Monitore CTR de cada link no painel", "Destaque seu produto ou curso principal"],
      en: ["Organize links by topic or platform", "Add email capture directly in bio", "Monitor each link CTR in the dashboard", "Highlight your main product or course"],
    },
    accent: "#7b61ff",
  },
  {
    id: "musicians",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Músico se apresentando no palco", en: "Musician performing on stage" },
    hook: { pt: "Lançamentos que convertem", en: "Launches that convert" },
    title: { pt: "Músicos & Artistas", en: "Musicians & Artists" },
    description: {
      pt: "Lance faixas, merch, ingressos e drops exclusivos em uma só página de alta conversão.",
      en: "Launch tracks, merch, tickets and exclusive drops on one high-converting page.",
    },
    steps: {
      pt: ["Links para Spotify, YouTube e Apple Music", "Loja de merch e venda de ingressos integrada", "Formulário de fãs VIP e newsletter", "Analytics para saber o que mais engaja"],
      en: ["Links to Spotify, YouTube and Apple Music", "Integrated merch store and ticket sales", "VIP fan form and newsletter", "Analytics to know what engages most"],
    },
    accent: "#ff3d8a",
  },
  {
    id: "coaches",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Coach ou consultora profissional", en: "Professional coach or consultant" },
    hook: { pt: "Mais reuniões qualificadas", en: "More qualified bookings" },
    title: { pt: "Coaches & Consultores", en: "Coaches & Consultants" },
    description: {
      pt: "Substitua links espalhados por um hub premium que gera confiança e mais calls qualificadas.",
      en: "Replace scattered links with a premium hub that builds trust and drives qualified discovery calls.",
    },
    steps: {
      pt: ["Agendamento em destaque no topo da bio", "Seções separadas por tipo de serviço", "Depoimentos e prova social integrados", "Analytics para rastrear a origem dos leads"],
      en: ["Scheduling highlighted at the top of bio", "Separate sections per service type", "Integrated testimonials and social proof", "Analytics to track lead sources"],
    },
    accent: "#00c9a7",
  },
  {
    id: "brands",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Vitrine de marca e produto", en: "Brand and product showcase" },
    hook: { pt: "Vitrine pronta para campanha", en: "Campaign-ready storefront" },
    title: { pt: "Pequenas Marcas", en: "Small Brands" },
    description: {
      pt: "Rode campanhas, destaque produtos e ofertas sazonais em um destino de marca otimizado para ação.",
      en: "Run campaigns, highlight products and seasonal offers from one action-optimized branded destination.",
    },
    steps: {
      pt: ["Coleções de produtos em destaque na bio", "Links UTM por campanha rastreados", "WhatsApp e delivery direto na bio", "Atualize promoções sem trocar o link da bio"],
      en: ["Featured product collections in bio", "UTM links tracked per campaign", "WhatsApp and delivery direct in bio", "Update promotions without changing the bio link"],
    },
    accent: "#ff6b4a",
  },
  {
    id: "fitness",
    image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Personal trainer em academia", en: "Personal trainer at gym" },
    hook: { pt: "Mais alunos todo mês", en: "More students every month" },
    title: { pt: "Personal Trainers", en: "Personal Trainers" },
    description: {
      pt: "Atraia novos alunos, venda planilhas e divulgue serviços tudo em uma página focada em conversão.",
      en: "Attract new students, sell workout plans and promote services all on one conversion-focused page.",
    },
    steps: {
      pt: ["Link direto para aula experimental", "Loja de planilhas e protocolos de treino", "Galeria de transformações como prova social", "Captura de leads com oferta de treino grátis"],
      en: ["Direct link to trial class booking", "Store for workout plans and protocols", "Transformation gallery as social proof", "Lead capture with free workout offer"],
    },
    accent: "#ffaa2c",
  },
  {
    id: "photographers",
    image: "https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Fotógrafo com câmera profissional", en: "Photographer with professional camera" },
    hook: { pt: "Portfólio que vende", en: "Portfolio that sells" },
    title: { pt: "Fotógrafos", en: "Photographers" },
    description: {
      pt: "Mostre seu portfólio, receba orçamentos e venda presets em um lugar único e profissional.",
      en: "Showcase your portfolio, receive quotes and sell presets in one professional place.",
    },
    steps: {
      pt: ["Link para portfólio e galeria online", "Formulário de orçamento integrado", "Mini loja de presets e arquivos digitais", "Depoimentos de clientes em destaque"],
      en: ["Link to online portfolio and gallery", "Integrated quote request form", "Mini store for presets and digital files", "Featured client testimonials"],
    },
    accent: "#00b4d8",
  },
  {
    id: "restaurants",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Prato gourmet de restaurante", en: "Gourmet restaurant dish" },
    hook: { pt: "Mais reservas e pedidos", en: "More reservations and orders" },
    title: { pt: "Restaurantes & Bares", en: "Restaurants & Bars" },
    description: {
      pt: "Cardápio digital, reservas e promoções do dia em uma página rápida e elegante.",
      en: "Digital menu, online reservations and daily specials on one fast and elegant page.",
    },
    steps: {
      pt: ["Cardápio digital atualizado em tempo real", "Botão de reserva de mesa direto", "Promoções e eventos da semana em destaque", "Delivery e WhatsApp para pedidos"],
      en: ["Real-time updated digital menu", "Direct table reservation button", "Weekly promotions and events highlighted", "Delivery and WhatsApp for orders"],
    },
    accent: "#e63946",
  },
  {
    id: "educators",
    image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=480&h=320&fit=crop&auto=format",
    imageAlt: { pt: "Educadora ensinando turma online", en: "Educator teaching online class" },
    hook: { pt: "Mais alunos matriculados", en: "More enrolled students" },
    title: { pt: "Educadores & Professores", en: "Educators & Teachers" },
    description: {
      pt: "Divulgue cursos, lives, materiais grátis e canais de ensino de forma organizada e profissional.",
      en: "Promote courses, live classes, free materials and teaching channels in an organized and professional way.",
    },
    steps: {
      pt: ["Link para plataforma do curso principal", "Formulário de interesse para próximas turmas", "Materiais grátis como isca digital", "Links para YouTube, podcast e comunidade"],
      en: ["Link to main course platform", "Interest form for upcoming cohorts", "Free materials as lead magnet", "Links to YouTube, podcast and community"],
    },
    accent: "#7209b7",
  },
];

/* ─────────────────────────────── Card ─────────────────────────────── */

type NicheCardProps = {
  niche: Niche;
  index: number;
  language: string;
  isFlipped: boolean;
  onFlip: () => void;
  shouldReduceMotion: boolean;
};

function NicheCard({ niche, index, language, isFlipped, onFlip, shouldReduceMotion }: NicheCardProps) {
  const lang = language as "pt" | "en";
  const title = pickByLanguage(lang, niche.title);
  const hook = pickByLanguage(lang, niche.hook);
  const description = pickByLanguage(lang, niche.description);
  const steps = pickByLanguage(lang, niche.steps);
  const imageAlt = pickByLanguage(lang, niche.imageAlt);
  const flipDuration = shouldReduceMotion ? 0 : 0.55;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.07, 0.35) }}
      className="h-full"
    >
      <div
        className={`relative rounded-[22px] border border-[#eadbcc] bg-white ${CARD_SHADOW} ${CARD_SHADOW_HOVER} cursor-pointer select-none h-full transition-colors duration-200 ease-out`}
        onClick={onFlip}
        role="button"
        aria-label={lang === "pt" ? `Ver como usar: ${title}` : `See how to use: ${title}`}
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onFlip()}
      >
        {/* 3D Flip container */}
        <div className="relative h-[420px] rounded-[22px] overflow-hidden" style={{ perspective: "1100px" }}>
          {/* ── FRONT ── */}
          <motion.div
            animate={{ rotateY: isFlipped ? 180 : 0 }}
            transition={{ duration: flipDuration, ease: [0.22, 1, 0.36, 1] }}
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
            className="absolute inset-0 bg-white rounded-[22px] overflow-hidden flex flex-col"
          >
            {/* Photo — inset framed */}
            <div className="px-3 pt-3 shrink-0">
              <div className="relative h-44 rounded-[14px] overflow-hidden">
                <img
                  src={niche.image}
                  alt={imageAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <span
                  className="absolute top-2.5 left-2.5 rounded-full text-[11px] font-semibold px-3 py-1.5 text-white"
                  style={{ background: niche.accent }}
                >
                  {hook}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-5 flex flex-col">
              <h3 className="font-editorial font-semibold text-[1.05rem] text-[#201814] mb-2 leading-snug">{title}</h3>
              <p className="text-sm text-[#66584d] leading-relaxed flex-1">{description}</p>
              <div className="flex items-center gap-1.5 text-[11px] text-[#9a8a7d] mt-4 pt-3 border-t border-[#f0e3d7]">
                <Lightbulb className="h-3.5 w-3.5 shrink-0 text-[#ef7b43]" />
                {lang === "pt" ? "Clique para ver como usar" : "Click to see how to use"}
              </div>
            </div>
          </motion.div>

          {/* ── BACK ── */}
          <motion.div
            animate={{ rotateY: isFlipped ? 0 : -180 }}
            transition={{ duration: flipDuration, ease: [0.22, 1, 0.36, 1] }}
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
            className="absolute inset-0 bg-[#fff7ef] rounded-[22px] overflow-hidden flex flex-col p-5"
          >
            <div className="mb-4">
              <span
                className="text-[11px] font-semibold rounded-full px-3 py-1 inline-block mb-3"
                style={{
                  background: `${niche.accent}1a`,
                  color: niche.accent,
                  border: `1px solid ${niche.accent}40`,
                }}
              >
                {lang === "pt" ? "Como usar o Qlivar" : "How to use Qlivar"}
              </span>
              <h3 className="font-editorial font-semibold text-[1.05rem] text-[#201814] leading-snug">{title}</h3>
            </div>

            <ol className="flex-1 space-y-3">
              {steps.map((step, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span
                    className="shrink-0 h-5 w-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold mt-0.5"
                    style={{ background: niche.accent }}
                  >
                    {i + 1}
                  </span>
                  <span className="text-sm text-[#493c34] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            <div className="flex items-center gap-1.5 text-[11px] text-[#9a8a7d] mt-4 pt-3 border-t border-[#f0e3d7]">
              <RotateCcw className="h-3.5 w-3.5 shrink-0" />
              {lang === "pt" ? "Clique para voltar" : "Click to go back"}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────── Section ─────────────────────────────── */

const CARD_W_REM = 18;        // matches "18rem" per card
const GAP_REM = 0.75;      // gap-3 = 0.75rem
const ANIM_S = 0.42;      // slide animation duration (seconds)
const AUTO_MS = 3000;

export function NicheSection() {
  const { language } = useLanguage();
  const shouldReduceMotion = useReducedMotion() ?? false;
  const N = niches.length;
  // CLONES = full copy of the list on each side so there's never empty space
  const CLONES = N;

  // Extended list: [last-N clones, ...real, first-N clones]
  const extended = useMemo(
    () => [...niches.slice(-CLONES), ...niches, ...niches.slice(0, CLONES)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // displayIdx: CLONES → first real card, CLONES+N-1 → last real card
  const [displayIdx, setDisplayIdx] = useState(CLONES);
  const displayIdxRef = useRef(CLONES);
  const [instant, setInstant] = useState(true); // disables transition for teleport + initial position
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());

  // Measured layout values — useLayoutEffect runs synchronously so first paint is correct
  const containerRef = useRef<HTMLDivElement>(null);
  const [stride, setStride] = useState(0);
  const [containerW, setContainerW] = useState(0);
  const [cardPx, setCardPx] = useState(0);

  const measure = useCallback(() => {
    if (!containerRef.current) return;
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
    setCardPx(CARD_W_REM * fs);
    setStride((CARD_W_REM + GAP_REM) * fs);
    setContainerW(containerRef.current.clientWidth);
  }, []);

  useLayoutEffect(() => {
    measure();
    // allow animations only after first correct position is painted
    requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
  }, [measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // translateX that centres extended[idx] in the container
  const getX = (idx: number) =>
    stride === 0 ? 0 : -(idx * stride) + containerW / 2 - cardPx / 2;

  const realIdx = ((displayIdx - CLONES) % N + N) % N;
  const animDur = shouldReduceMotion ? 0 : ANIM_S;

  /* ── navigate ── */
  const isAnimRef = useRef(false);
  const navigate = useCallback(
    (newIdx: number) => {
      if (isAnimRef.current) return;
      isAnimRef.current = true;
      displayIdxRef.current = newIdx;
      setDisplayIdx(newIdx);

      setTimeout(() => {
        isAnimRef.current = false;
        // After animation, silently teleport if we're on a clone
        let corrected = newIdx;
        if (newIdx < CLONES) corrected = newIdx + N;
        else if (newIdx >= CLONES + N) corrected = newIdx - N;
        if (corrected !== newIdx) {
          setInstant(true);
          displayIdxRef.current = corrected;
          setDisplayIdx(corrected);
          requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
        }
      }, animDur * 1000 + 80);
    },
    [animDur, N],
  );

  /* ── auto-advance ── */
  const isHoveredRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (shouldReduceMotion) return;
    timerRef.current = setInterval(() => {
      if (!isHoveredRef.current) navigate(displayIdxRef.current + 1);
    }, AUTO_MS);
  }, [shouldReduceMotion, navigate]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  const handleNav = useCallback(
    (newIdx: number) => { navigate(newIdx); resetTimer(); },
    [navigate, resetTimer],
  );

  /* ── touch / swipe ── */
  const touchRef = useRef<{ startX: number; startY: number; startTime: number } | null>(null);
  const SWIPE_THRESHOLD = 40;  // min px to count as a swipe

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, startTime: Date.now() };
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    touchRef.current = null;

    // ignore if it was a vertical scroll or too small
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;

    if (dx < 0) {
      handleNav(displayIdxRef.current + 1); // swipe left → next
    } else {
      handleNav(displayIdxRef.current - 1); // swipe right → prev
    }
  }, [handleNav]);

  /* ── flip ── */
  const toggleFlip = (id: string) =>
    setFlippedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });

  return (
    <section id="niches" className="bg-[#fff7ef] py-14 sm:py-20 lg:py-24 overflow-hidden">
      {/* ── Header ── */}
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.24em] text-[#9a7258]">
            {language === "pt" ? "Casos de uso feitos para resultado" : "Use cases built for outcomes"}
          </p>
          <h2 className="mt-4 font-editorial font-semibold text-[clamp(2rem,6vw,4rem)] leading-[1.05] sm:leading-[1.02] tracking-[-0.035em] text-[#201814]">
            {language === "pt" ? (
              <>Construído para a forma como{" "}<span className="text-gradient-creator">creators vendem e crescem</span></>
            ) : (
              <>Built for the way{" "}<span className="text-gradient-creator">creators sell and grow</span></>
            )}
          </h2>
          <p className="mt-5 text-[15px] leading-7 text-[#66584d] sm:text-lg sm:leading-8">
            {language === "pt"
              ? "Pare de mandar sua audiência para páginas genéricas de links. Dê uma experiência focada para aumentar confiança, cliques e receita."
              : "Stop sending your audience to generic link pages. Give them a focused experience designed to increase trust, clicks, and revenue."}
          </p>
        </motion.div>
      </div>

      {/* ── Carousel ── */}
      <div className="mx-auto max-w-[1600px] pb-1 pt-8">
        <div
          ref={containerRef}
          className="relative overflow-hidden"
          onMouseEnter={() => { isHoveredRef.current = true; }}
          onMouseLeave={() => { isHoveredRef.current = false; }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Edge fade masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-4 sm:w-8 lg:w-16 z-10
          bg-gradient-to-r from-[#fff7ef] via-[#fff7ef]/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-4 sm:w-8 lg:w-16 z-10
          bg-gradient-to-l from-[#fff7ef] via-[#fff7ef]/80 to-transparent" />

          {/* Sliding track — translateX controlled by framer-motion */}
          <motion.div
            className="flex py-6"
            style={{ gap: `${GAP_REM}rem` }}
            animate={{ x: getX(displayIdx) }}
            transition={instant ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 30, duration: animDur }}
          >
            {extended.map((niche, i) => (
              <div
                key={`${niche.id}-${i}`}
                style={{ width: `${CARD_W_REM}rem`, flexShrink: 0 }}
              >
                <NicheCard
                  niche={niche}
                  index={i}
                  language={language}
                  isFlipped={flippedCards.has(`${niche.id}-${i}`)}
                  onFlip={() => toggleFlip(`${niche.id}-${i}`)}
                  shouldReduceMotion={shouldReduceMotion}
                />
              </div>
            ))}
          </motion.div>

          <CarouselArrow
            direction="left"
            onClick={() => handleNav(displayIdx - 1)}
            ariaLabel={language === "pt" ? "Card anterior" : "Previous card"}
            className="flex sm:hidden"
          />
          <button
            onClick={() => handleNav(displayIdx - 1)}
            aria-label={language === "pt" ? "Card anterior" : "Previous card"}
            className="absolute left-4 top-[44%] -translate-y-1/2 z-20
            hidden sm:flex items-center justify-center
            h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm border border-[#eadbcc] shadow-sm
            text-[#493c34] hover:bg-[#ffe6d3] hover:text-[#ef7b43] hover:ring-1 hover:ring-[#ef7b43]/30
            transition-[background,color,box-shadow] duration-150"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <CarouselArrow
            direction="right"
            onClick={() => handleNav(displayIdx + 1)}
            ariaLabel={language === "pt" ? "Próximo card" : "Next card"}
            className="flex sm:hidden"
          />
          <button
            onClick={() => handleNav(displayIdx + 1)}
            aria-label={language === "pt" ? "Próximo card" : "Next card"}
            className="absolute right-4 top-[44%] -translate-y-1/2 z-20
            hidden sm:flex items-center justify-center
            h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm border border-[#eadbcc] shadow-sm
            text-[#493c34] hover:bg-[#ffe6d3] hover:text-[#ef7b43] hover:ring-1 hover:ring-[#ef7b43]/30
            transition-[background,color,box-shadow] duration-150"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>{/* ── end carousel container ── */}

      {/* ── Dots + hint ── */}
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6">
        <CarouselDots
          count={niches.length}
          activeIndex={realIdx}
          onDotClick={(i) => handleNav(i + CLONES)}
          ariaLabelFn={(i) => `Card ${i + 1}`}
        />

        <CarouselHint
          text={language === "pt" ? "← Deslize para ver mais →" : "← Swipe to see more →"}
          className="sm:hidden"
        />
      </div>
    </section>
  );
}

